/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type JSX, useCallback, useMemo } from 'react';
import { useLocation, useNavigation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@/hooks/use-navigate';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getPaginationItems } from '@/lib/pagination-utils';

export default function CategoryPagination({
    limit,
    offset,
    total,
}: {
    limit: number;
    offset: number;
    total: number;
}): JSX.Element | null {
    const navigate = useNavigate();
    const location = useLocation();
    const navigation = useNavigation();
    const { t } = useTranslation('category');
    const isPending = navigation.state !== 'idle';

    /**
     * Optimistic offset derived from the in-flight navigation target.
     *
     * While a navigation is pending, `navigation.location` holds the target location, allowing us to read the
     * intended offset param immediately. Once the navigation settles, we fall back to the server-provided value.
     */
    const effectiveOffset = navigation.location
        ? Number(new URLSearchParams(navigation.location.search).get('offset') || offset)
        : offset;

    const totalPages = Math.ceil(total / limit);
    const current = Math.floor(effectiveOffset / limit) + 1;
    const pageNumbers = useMemo(() => getPaginationItems(totalPages, current), [totalPages, current]);

    const navigatePage = useCallback(
        (page: number) => {
            const params = new URLSearchParams(location.search);
            params.set('offset', String((page - 1) * limit));
            void navigate({
                ...location,
                search: `?${params.toString()}`,
            });
        },
        [location, navigate, limit]
    );

    if (totalPages <= 1) {
        return null;
    }

    return (
        <div className="flex justify-center">
            <nav
                data-testid="category-pagination"
                className={`type-label-medium flex items-center gap-1${isPending ? ' pointer-events-none opacity-50 transition-opacity' : ''}`}
                aria-label={t('pagination.label')}>
                {/* PREVIOUS IS NOT RENDERED ON PAGE 1 — measured, not assumed.
                    `Previous` counts 0 in every page-1 listing capture on disk, at
                    every width and on BOTH brands, each against a `div` positive
                    control in the same command: nm-plp-d-1280 (10,012 div),
                    nm-plp-m-440 (12,540), search-d-1280 (5,272), search-d-1024
                    (5,220), search-d-1440 (5,272), search-m-390 (6,328), search-m-440
                    (6,256), bg-plp-d-1280 (6,096), bg-plp-m-440 (3,132),
                    bg-search-d-1280 (5,008). The zero is real, not an extraction
                    artifact — the same file that yields it also yields `Next` (8 on
                    nm-plp-d-1280 = 4 real after halving the capture's double emission:
                    two pagers, each an `<a aria-label="Next">` wrapping a
                    `<span>Next</span>`).

                    This component is mounted by the SEARCH route as well as the PLP
                    (`routes/_app.search.tsx`), which is why the search captures are
                    listed above rather than the PLP ones alone — the control is absent
                    on both surfaces, so this is a plain removal and not a per-surface
                    prop.

                    What live puts in the slot is an EMPTY span, not nothing:
                        <nav aria-label="Pagination" …>
                          <span className="text-right"></span>      <- the Previous slot
                          <span aria-label="Page 1 of 82">1 of  82</span>
                          <span><a aria-label="Next">Next ▸</a></span>
                    (nm-plp-d-1280/App.tsx @65642). An empty inline span with no
                    content, border or padding paints nothing and takes no space, so
                    omitting the node is equivalent in the rendered box and avoids
                    shipping an empty element purely to mirror the extractor.

                    NEXT IS DELIBERATELY LEFT ALONE. Every capture on disk is page 1,
                    so live's last-page behaviour is UNMEASURED. Mirroring this guard
                    onto `Next` would be inference, not evidence — the symmetry is
                    plausible and that is exactly what makes it a guess. */}
                {current > 1 && (
                    <Button
                        variant="ghost"
                        className="h-9 cursor-pointer gap-1 px-2.5"
                        onClick={() => void navigatePage(current - 1)}
                        aria-label={t('pagination.previousPage')}>
                        <ChevronLeft className="size-4" />
                        <span>{t('pagination.previous')}</span>
                    </Button>
                )}

                {pageNumbers.map((item) =>
                    typeof item === 'object' ? (
                        <span
                            key={`ellipsis-${item.key}`}
                            className="flex size-9 items-center justify-center text-foreground/80">
                            ...
                        </span>
                    ) : (
                        <Button
                            key={item}
                            onClick={() => void navigatePage(item)}
                            disabled={current === item}
                            variant={current === item ? 'outline' : 'ghost'}
                            className="size-9 cursor-pointer"
                            aria-label={t('pagination.page', { number: item })}
                            aria-current={current === item ? 'page' : undefined}>
                            {item}
                        </Button>
                    )
                )}

                <Button
                    variant="ghost"
                    className="h-9 cursor-pointer gap-1 px-2.5"
                    onClick={() => void navigatePage(current + 1)}
                    disabled={current === totalPages}
                    aria-label={t('pagination.nextPage')}>
                    <span>{t('pagination.next')}</span>
                    <ChevronRight className="size-4" />
                </Button>
            </nav>
        </div>
    );
}
